import type {
  IPayrollCardOpeningApplication,
  IPayrollCardOpeningApplicationRequest,
} from "@/modules/application/common/types";
import {
  useCreatePayrollCardOpeningApplicationMutation,
  useGetPayrollCardOpeningApplicationQuery,
  useUpdatePayrollCardOpeningApplicationMutation
} from "@/modules/application/services";
import { INPUT_MAX_LIMIT } from "@/shared/common/constants";
import type { IExtendedObject, IOperationPageProps } from "@/shared/common/types";
import { ApplicationLayout } from "@/shared/components/ApplicationLayout";
import { isValidPhoneNumber, trimAll } from "@/shared/helpers";
import { formatPhoneNumber } from "@/shared/helpers/commonHelpers";
import { useHasFormChanges } from "@/shared/hooks";
import { CollapseGroup } from "@ab.uitools/ui-library/components/Collapse";
import { FormField } from "@ab.uitools/ui-library/components/FormField";
import { Input } from "@ab.uitools/ui-library/components/Input";
import { Textarea } from "@ab.uitools/ui-library/components/Textarea";
import { useFormProps } from "@ab.uitools/ui-library/hooks/useFormProps";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import uuid4 from "uuid4";

export const PayrollCardOpeningApplicationForm = ({ isEdit, isCopy }: IOperationPageProps) => {
  const { t } = useTranslation();
  const { operationId = "" } = useParams();
  const idempotencyKey = useRef(uuid4()).current;
  const { errors = {}, reset, watch } = useFormProps();

  const [defaultData, setDefaultData] = useState<IExtendedObject>({});

  const {
    data: payrollCardOpeningApplication,
    isLoading: isGetApplicationLoading,
  } = useGetPayrollCardOpeningApplicationQuery({
    id: operationId
  }, {
    refetchOnMountOrArgChange: true,
    skip: (!isEdit || !isCopy) && !operationId
  });

  const [
    createPayrollCardOpeningApplicationApi,
    { isLoading: isCreateLoading },
  ] = useCreatePayrollCardOpeningApplicationMutation();

  const [
    updatePayrollCardOpeningApplicationApi,
    { isLoading: isUpdateLoading },
  ] = useUpdatePayrollCardOpeningApplicationMutation();

  const payrollCardOpeningApplicationAction = useCallback((values: IPayrollCardOpeningApplication) => {
    const requestData: IPayrollCardOpeningApplicationRequest = {
      ...values,
      phoneNumber: trimAll(values.phoneNumber),
    };

    return isEdit ? updatePayrollCardOpeningApplicationApi({
        data: requestData,
        id: `${payrollCardOpeningApplication?.id}`,
      }).unwrap() :
      createPayrollCardOpeningApplicationApi({
        data: requestData,
        idempotencyKey,
      }).unwrap();
  }, [payrollCardOpeningApplication]);

  const values = watch?.() || {};

  const setDefaultValues = () => {
    const data = {
      fullName: payrollCardOpeningApplication?.fullName,
      ssn: payrollCardOpeningApplication?.ssn,
      address: payrollCardOpeningApplication?.address,
      email: payrollCardOpeningApplication?.email,
      phoneNumber: payrollCardOpeningApplication?.phoneNumber,
      purpose: payrollCardOpeningApplication?.purpose,
      applicationNumber: isEdit ?
        payrollCardOpeningApplication?.applicationNumber : values.applicationNumber,
    };
    reset?.(data);
    setDefaultData(data);
  };

  const currentData = useMemo(() => ({
    purpose: values.purpose,
    fullName: values.fullName,
    ssn: values.ssn,
    address: values.address,
    email: values.email,
    phoneNumber: values.phoneNumber,
    applicationNumber: values.applicationNumber,
  }), [values]);

  const hasChanges = useHasFormChanges(defaultData, currentData);

  useEffect(() => {
    if ((isEdit || isCopy) && !!payrollCardOpeningApplication?.id) {
      setDefaultValues();
    }
  }, [payrollCardOpeningApplication]);

  return (
    <ApplicationLayout
      title={t("application.payrollCardOpening.title")}
      page="Payroll card opening application"
      isEdit={isEdit}
      hasChanges={hasChanges}
      isLoading={isCreateLoading || isUpdateLoading}
      isGetApplicationLoading={isGetApplicationLoading}
      getApplicationId={payrollCardOpeningApplicationAction}
    >
      <CollapseGroup
        className="collapse-group"
        items={[
          {
            content: <>
              <FormField
                name="fullName"
                className="mb-32"
                errorMessageIcon="infoFilled"
                As={(props) => (
                  <Input
                    {...props}
                    required
                    label={t("application.fullName")}
                    placeholder={t("application.enterFullName")}
                  />
                )}
              />
              <FormField
                name="ssn"
                className="mb-32"
                errorMessageIcon="infoFilled"
                As={(props) => (
                  <Input
                    {...props}
                    required
                    label={t("application.SSN")}
                    placeholder={t("application.ssnPlaceholder")}
                  />
                )}
              />
              <FormField
                name="address"
                className="mb-32"
                errorMessageIcon="infoFilled"
                As={(props) => (
                  <Input
                    {...props}
                    required
                    label={t("application.registrationAddress")}
                    placeholder={t("application.enterRegistrationAddress")}
                  />
                )}
              />
              <FormField
                name="email"
                className="mb-32"
                errorMessageIcon="infoFilled"
                As={(props) => (
                  <Input
                    {...props}
                    required
                    label={t("general.email")}
                    placeholder={t("general.enterEmail")}
                  />
                )}
              />
              <FormField
                name="phoneNumber"
                className="mb-32"
                errorMessageIcon="infoFilled"
                As={(props) => {
                  const { onChange, ...rest } = props;
                  return (
                    <Input
                      {...rest}
                      required
                      label={t("application.mobilePhoneNumber")}
                      placeholder={t("application.enterPhoneNumber")}
                      onChange={(e) => {
                        e.target.value = formatPhoneNumber(e.target.value);
                        onChange?.(e);
                      }}
                      isAllowed={({ formattedValue }) => isValidPhoneNumber(formattedValue)}
                    />
                  );
                }}
              />
              <FormField
                name="purpose"
                className="mb-8"
                errorMessageIcon="infoFilled"
                hideErrorMessage
                As={(props) => (
                  <Textarea
                    {...props}
                    required
                    error={`${errors?.purpose?.message || ""}`}
                    label={t("general.purpose")}
                    placeHolder={t("general.enterYourPurpose")}
                    maxCount={INPUT_MAX_LIMIT}
                  />
                )}
              />
            </>,
            title: t("application.applicationDetails"),
            value: "application_details",
            isOpen: true,
          },
          {
            content: <>
              <FormField
                name="applicationNumber"
                className="mb-8"
                errorMessageIcon="infoFilled"
                As={(props) => (
                  <Input
                    {...props}
                    required
                    label={t("application.applicationNumber")}
                    placeholder={t("application.enterApplicationNumber")}
                  />
                )}
              />
            </>,
            title: t("general.otherDetails"),
            value: "other_details",
            isOpen: true,
          },
        ]}
      />
    </ApplicationLayout>
  );
};
